import { useEffect, useState } from 'react';

// material-ui
import Accordion from '@mui/material/Accordion';
import Alert from '@mui/material/Alert';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

// project imports
import useAuth from 'hooks/useAuth';
import useExclusiveAccordion from 'utils/useExclusiveAccordion';
import { hasModuleAccess } from 'utils/roleWeights';
import { getSubscriptionStatus, isPushSupported, subscribeToPush, unsubscribeFromPush } from 'api/push';
import { emailTestSignature, getSignatureTestStatus } from 'api/signatures';
import SignatureCapture from 'components/signatures/SignatureCapture';
import PinKeypad from 'components/PinKeypad';
import { PIN_PATTERN } from 'utils/pinConfig';

// assets
import {
  IconNotifications,
  IconExpandMore,
  IconLocation,
  IconLock,
  IconMail,
  IconPin,
  IconPrivateEmail,
  IconPunchClock,
  IconSettings,
  IconSignature,
  IconClose
} from 'utils/icons';

// ==============================|| PROFILE - NASTAVENÍ UŽIVATELE (ACCORDION) ||============================== //
// "Změnit heslo" i "Změnit PIN" obě jedou přes e-mail s odkazem
// (/auth/forgot-password, /account/pin/change-request) - žádná z nich
// nemění hodnotu v téhle relaci přímo, obě jsou skutečně funkční, ne
// placeholder.
//
// "Push notifikace": HOTOVO, skutečný backend (2026-08-22, Push modul) -
// stav se čte z /push/status (má TENTO účet aktivní subscription na
// serveru?), ne z holého Notification.permission. Oprava reálného bugu:
// Notification.permission je oprávnění PROHLÍŽEČE pro tuto doménu na tomto
// zařízení, sdílené mezi všemi účty na směnovém/sdíleném PC - nový
// uživatel dřív viděl "povoleno", i když sám nikdy nic nepovolil, jen proto
// že kolega dřív klikl "povolit" ve stejném prohlížeči. Teď je stav vázaný
// na konkrétní účet (push_subscriptions.user_id), viz api/push.js.
//
// "GPS lokace" ZŮSTÁVÁ jen zrcadlem oprávnění prohlížeče
// (Permissions API) - stejný sdílený-PC problém tu principiálně existuje
// dál, ale skutečná per-účet vazba nedává smysl bez modulu Docházka
// (clock-in/out), který zatím vůbec neexistuje - vědomě mimo rozsah Push
// modulu, viz docs/ARCHITECTURE.md.
//
// "E-mail o přihlášení" (2026-08-22, core_users.login_email_notify,
// výchozí VYPNUTO) - opt-in preference, samostatná od nepodmíněného
// "nové zařízení" e-mailu (AuthService::trackDeviceBestEffort()). Zapnutá
// pošle e-mail při KAŽDÉM úspěšném přihlášení, ne jen z nerozpoznaného
// zařízení.
//
// Všech pět řádků (Změnit heslo / Změnit PIN / Push / GPS / E-mail) jsou od
// 2026-08-23 každý svůj vlastní vnořený accordion (na výslovné přání
// uživatele - "omezit náhodné kliknutí a zbytečné odesílání e-mailu"), ne
// přímo klikatelný řádek jako dřív - hlavička nese jen ikonu a název,
// samotná akce je až tlačítko uvnitř rozbaleného těla. U Push/GPS/E-mailu/PIN
// ikona nese STAV (zelená povoleno / červená přeškrtnutá zakázáno - `enabled`
// prop, u PIN totéž co u "Test podpisu" - stejný `hasPin`); Změnit heslo
// nemá žádný "stav" k zobrazení (appka nikde neeviduje, jak "silné" nebo
// staré heslo je), takže tam se `enabled` nepředává a ikona zůstává
// neutrální černá/`text.primary` (StatusIcon to rozliší podle
// `enabled === undefined`).
//
// Viditelnost jednotlivých položek (2026-08-29, real bug fixed - dřív se
// tu ukazovalo úplně všech pět řádků každému, bez ohledu na to, jestli má
// uživatel k dané věci vůbec nějaké oprávnění):
//  - Změnit heslo / E-mail o přihlášení: VŽDY, bez ohledu na moduly - obě
//    jsou bezpečnostní nastavení VLASTNÍHO účtu (přihlašovací heslo,
//    upozornění na přihlášení), ne obchodní funkce vázaná na nějaký modul.
//  - Změnit PIN: jen s core_modules 'pin' (libovolná role - core_modules.pin.
//    description to říká doslova: "Držení libovolné role zde znamená, že má
//    uživatel povinnost nastavit si přihlašovací PIN."). Toto zůstává ČISTĚ
//    požadavek na PIN, ne povolení k podpisům - záměrně nezávislé (2026-08-29,
//    na výslovné přání uživatele - "PIN bude potřeba, aniž bychom něco
//    podepisovali").
//  - Test podpisu: vlastní core_modules 'signature', NE 'pin' - dřív obojí
//    sdílelo bránu 'pin', což mylně naznačovalo, že kdo musí mít PIN, umí i
//    podepisovat. PIN zůstává jen VNITŘNÍ ověřovací krok, který podpis
//    interně vyžaduje (SignatureService::capture() volá PinService::verify()),
//    ne totéž jako právo podpis použít - proto oddělený modul/role. Bez PIN
//    (core_modules 'pin') podpis pořád nejde dokončit, to řeší UI stavem
//    "Nejdřív si nastavte PIN" (viz `hasPin === false` disabled/description
//    níže), ne skrytím celé položky.
//  - Push notifikace: jen s core_modules 'push' (čtenář = smí přijímat).
//  - GPS lokace: jen s core_modules 'attendance' - jediný skutečný
//    konzument je geo-ověření při píchnutí v AttendanceAccordion.jsx, viz
//    poznámka u "GPS lokace" výše ("čeká na modul Docházka").

function StatusIcon({ icon: Icon, enabled }) {
  const neutral = enabled === undefined;
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        lineHeight: 0,
        color: neutral ? 'text.primary' : enabled ? 'success.main' : 'error.main'
      }}
    >
      <Icon style={{ fontSize: '1.3rem' }} />
      {!neutral && !enabled && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '140%',
            height: '2px',
            bgcolor: 'currentColor',
            transform: 'translate(-50%, -50%) rotate(-45deg)'
          }}
        />
      )}
    </Box>
  );
}

function SettingAccordion({ icon, enabled, label, description, actionLabel, onAction, busy, disabled, error, success, expanded, onChange }) {
  return (
    <Accordion disableGutters elevation={0} square expanded={expanded} onChange={onChange} sx={{ '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<IconExpandMore style={{ fontSize: '0.9rem' }} />} sx={{ px: 2.5, minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.25 }}>
          <StatusIcon icon={icon} enabled={enabled} />
          <Typography variant="body2">{label}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2.5, pt: 1.25, pb: 1.5, borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack sx={{ gap: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            {description}
          </Typography>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            onClick={onAction}
            disabled={busy || disabled}
            sx={{ textTransform: 'none' }}
          >
            {busy ? <CircularProgress size={16} /> : actionLabel}
          </Button>
          {error && (
            <Typography variant="caption" sx={{ color: 'error.main' }}>
              {error}
            </Typography>
          )}
          {!error && success && (
            <Typography variant="caption" sx={{ color: 'success.main' }}>
              {success}
            </Typography>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default function UserSettingsAccordion({ expanded: sectionExpanded, onChange: onSectionChange }) {
  const {
    user,
    forgotPassword,
    requestPinChange,
    getNotificationSettings,
    setLoginEmailNotify,
    getAttendanceReminderSettings,
    setAttendanceReminderEnabled,
    getPinStatus,
    getPrivateEmailStatus,
    requestPrivateEmail,
    revealPrivateEmail
  } = useAuth();
  const theme = useTheme();
  // "první část přes celý telefon" (2026-08-23, na výslovné přání uživatele) -
  // podpis potřebuje reálný prostor na kreslení, na mobilu přes celou obrazovku.
  const signatureModalFullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const canSeePin = hasModuleAccess(user?.permissions, 'pin', 'reader');
  const canSeeSignatures = hasModuleAccess(user?.permissions, 'signature', 'reader');
  const canSeePush = hasModuleAccess(user?.permissions, 'push', 'reader');
  const canSeeGps = hasModuleAccess(user?.permissions, 'attendance', 'reader');

  const [passwordSent, setPasswordSent] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const [pinSent, setPinSent] = useState(false);
  const [pinError, setPinError] = useState('');

  // undefined = ještě nenačteno (StatusIcon zůstane neutrální, ne rovnou
  // červená), viz StatusIcon's `enabled === undefined` větev výše. Bez PIN
  // "Test podpisu" nejde vůbec dokončit (SignatureService::capture()
  // vyžaduje PinService::verify()) - ikona to má ukázat na první pohled,
  // ne až po kliknutí a chybové hlášce (2026-08-23, na výslovné přání
  // uživatele).
  const [hasPin, setHasPin] = useState(undefined);

  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState('');

  const [gpsGranted, setGpsGranted] = useState(false);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [gpsError, setGpsError] = useState('');

  const [emailNotifyEnabled, setEmailNotifyEnabled] = useState(false);
  const [emailNotifyBusy, setEmailNotifyBusy] = useState(false);
  const [emailNotifyError, setEmailNotifyError] = useState('');

  // "Alarm docházky" (2026-08-31, na výslovné přání uživatele) - stejný vzor jako "E-mail o přihlášení" výše.
  const [attendanceReminderEnabled, setAttendanceReminderEnabledState] = useState(false);
  const [attendanceReminderBusy, setAttendanceReminderBusy] = useState(false);
  const [attendanceReminderError, setAttendanceReminderError] = useState('');

  // Soukromý e-mail (2026-08-30) - cíl pro zaslání podepsané docházkové
  // karty. undefined = ještě nenačteno (neutrální ikona), stejný vzor jako
  // hasPin/testConfirmed výše. Appka si od backendu NIKDY nežádá/nedrží
  // dešifrovanou adresu jen tak - status() ji ani nevrací (explicit
  // security request 2026-08-30: "veřejně by v apce neměl být vůbec nikde
  // viděn"), jen is_set/confirmed_at. Adresu jde vidět jedině přes
  // revealedPrivateEmail (PIN-gated dialog, viz handleRevealPrivateEmail).
  const [privateEmailIsSet, setPrivateEmailIsSet] = useState(undefined);
  const [privateEmailConfirmedAt, setPrivateEmailConfirmedAt] = useState(undefined);
  const [privateEmailInput, setPrivateEmailInput] = useState('');
  const [privateEmailBusy, setPrivateEmailBusy] = useState(false);
  const [privateEmailError, setPrivateEmailError] = useState('');
  const [privateEmailSuccess, setPrivateEmailSuccess] = useState('');

  const [revealDialogOpen, setRevealDialogOpen] = useState(false);
  const [revealPin, setRevealPin] = useState('');
  const [revealedPrivateEmail, setRevealedPrivateEmail] = useState('');
  const [revealBusy, setRevealBusy] = useState(false);
  const [revealError, setRevealError] = useState('');

  const [expandedSetting, handleSettingAccordionChange] = useExclusiveAccordion();

  const [signatureTestOpen, setSignatureTestOpen] = useState(false);
  const [signatureTestSuccess, setSignatureTestSuccess] = useState('');
  const [signatureTestError, setSignatureTestError] = useState('');

  // undefined = ještě nenačteno, stejná neutrální ikona jako u hasPin. false
  // dokud se neklikne na odkaz v e-mailu (2026-08-23, real bug fixed -
  // ikona dřív svítila zeleně hned po odeslání testu, i když se nikdy
  // neprokázalo, že e-mail skutečně dorazil - viz
  // SignatureRepository::latestTestConfirmedForUser()'s docblock).
  const [testConfirmed, setTestConfirmed] = useState(undefined);

  useEffect(() => {
    // Test podpisu potřebuje `hasPin` i když je 'signature' udělené bez 'pin'
    // (architektonicky teď oddělené role) - jinak by mu zůstal navždy
    // neutrální stav a mylně naznačoval, že podepsat může, i když bez PIN
    // to backend (PinService::verify()) stejně odmítne.
    if (!canSeePin && !canSeeSignatures) return;
    getPinStatus()
      .then((data) => setHasPin(Boolean(data.has_pin)))
      .catch(() => {});
  }, [canSeePin, canSeeSignatures, getPinStatus]);

  useEffect(() => {
    if (!canSeeSignatures) return;
    getSignatureTestStatus()
      .then((data) => setTestConfirmed(Boolean(data.confirmed)))
      .catch(() => {});
  }, [canSeeSignatures]);

  useEffect(() => {
    if (!canSeePush || !isPushSupported()) return;
    getSubscriptionStatus()
      .then((data) => setPushEnabled(Boolean(data.enabled)))
      .catch(() => {});
  }, [canSeePush]);

  useEffect(() => {
    if (!canSeeGps || !navigator.permissions?.query) return;
    navigator.permissions
      .query({ name: 'geolocation' })
      .then((status) => {
        setGpsGranted(status.state === 'granted');
        status.onchange = () => setGpsGranted(status.state === 'granted');
      })
      .catch(() => {});
  }, [canSeeGps]);

  useEffect(() => {
    getNotificationSettings()
      .then((data) => setEmailNotifyEnabled(Boolean(data.login_email_notify)))
      .catch(() => {});
  }, [getNotificationSettings]);

  useEffect(() => {
    if (!canSeeGps) return;
    getAttendanceReminderSettings()
      .then((data) => setAttendanceReminderEnabledState(Boolean(data.attendance_reminder_enabled)))
      .catch(() => {});
  }, [canSeeGps, getAttendanceReminderSettings]);

  const loadPrivateEmail = () => {
    getPrivateEmailStatus()
      .then((data) => {
        setPrivateEmailIsSet(Boolean(data.is_set));
        setPrivateEmailConfirmedAt(data.confirmed_at);
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadPrivateEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPrivateEmailStatus]);

  const handleChangePassword = async () => {
    setPasswordError('');
    try {
      await forgotPassword({
        identifier: user?.identifier,
        website: '',
        form_ts: Math.floor(Date.now() / 1000) - 5
      });
      setPasswordSent(true);
    } catch (error) {
      setPasswordError(error.message || 'Požadavek se nezdařil.');
    }
  };

  const handleChangePin = async () => {
    setPinError('');
    try {
      await requestPinChange();
      setPinSent(true);
    } catch (error) {
      setPinError(error.message || 'Požadavek se nezdařil.');
    }
  };

  const handlePushClick = async () => {
    if (!isPushSupported() || pushBusy) return;
    setPushBusy(true);
    setPushError('');
    try {
      if (pushEnabled) {
        await unsubscribeFromPush();
        setPushEnabled(false);
      } else {
        await subscribeToPush();
        setPushEnabled(true);
      }
    } catch (error) {
      setPushError(error.message || 'Požadavek se nezdařil.');
    } finally {
      setPushBusy(false);
    }
  };

  const handleGpsClick = () => {
    if (!navigator.geolocation || gpsBusy) return;
    setGpsBusy(true);
    setGpsError('');
    navigator.geolocation.getCurrentPosition(
      () => {
        setGpsGranted(true);
        setGpsBusy(false);
      },
      () => {
        setGpsGranted(false);
        setGpsError('Oprávnění nebylo uděleno.');
        setGpsBusy(false);
      }
    );
  };

  const handleEmailNotifyClick = async () => {
    if (emailNotifyBusy) return;
    setEmailNotifyBusy(true);
    setEmailNotifyError('');
    const next = !emailNotifyEnabled;
    try {
      await setLoginEmailNotify(next);
      setEmailNotifyEnabled(next);
    } catch (error) {
      setEmailNotifyError(error.message || 'Požadavek se nezdařil.');
    } finally {
      setEmailNotifyBusy(false);
    }
  };

  const handleAttendanceReminderClick = async () => {
    if (attendanceReminderBusy) return;
    setAttendanceReminderBusy(true);
    setAttendanceReminderError('');
    const next = !attendanceReminderEnabled;
    try {
      await setAttendanceReminderEnabled(next);
      setAttendanceReminderEnabledState(next);
    } catch (error) {
      setAttendanceReminderError(error.message || 'Požadavek se nezdařil.');
    } finally {
      setAttendanceReminderBusy(false);
    }
  };

  const handleRequestPrivateEmail = async () => {
    if (privateEmailBusy || !privateEmailInput.trim()) return;
    setPrivateEmailBusy(true);
    setPrivateEmailError('');
    setPrivateEmailSuccess('');
    try {
      await requestPrivateEmail(privateEmailInput.trim());
      setPrivateEmailSuccess('Potvrzovací e-mail byl odeslán - klikněte na odkaz v e-mailu, ať se adresa ověří.');
      setPrivateEmailInput('');
      setRevealedPrivateEmail('');
      loadPrivateEmail();
    } catch (error) {
      setPrivateEmailError(error.message || 'Požadavek se nezdařil.');
    } finally {
      setPrivateEmailBusy(false);
    }
  };

  const handleOpenReveal = () => {
    setRevealPin('');
    setRevealedPrivateEmail('');
    setRevealError('');
    setRevealDialogOpen(true);
  };

  const handleCloseReveal = () => {
    setRevealDialogOpen(false);
    setRevealPin('');
    setRevealedPrivateEmail('');
    setRevealError('');
  };

  const handleRevealPrivateEmail = async () => {
    if (revealBusy || !PIN_PATTERN.test(revealPin)) return;
    setRevealBusy(true);
    setRevealError('');
    try {
      setRevealedPrivateEmail(await revealPrivateEmail(revealPin));
    } catch (error) {
      setRevealError(error.message || 'PIN se nepodařilo ověřit.');
    } finally {
      setRevealBusy(false);
      setRevealPin('');
    }
  };

  const handleOpenSignatureTest = () => {
    setSignatureTestSuccess('');
    setSignatureTestError('');
    setSignatureTestOpen(true);
  };

  const handleSignatureCaptured = async (signatureId) => {
    try {
      await emailTestSignature(signatureId);
      // Nové odeslání = nové, zatím nepotvrzené kolo (ikona se má vrátit na
      // červenou, dokud se neklikne na odkaz v tomhle konkrétním e-mailu -
      // ne zůstat zeleně jen proto, že nějaké dřívější kolo bylo potvrzené).
      setTestConfirmed(false);
      setSignatureTestSuccess('Podpis byl uložen a odeslán na váš e-mail - test je úspěšný, až potvrdíte doručení kliknutím na odkaz v e-mailu.');
    } catch (error) {
      setSignatureTestError(error.message || 'Podpis se uložil, ale e-mail se odeslat nepodařilo.');
    }
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      square
      expanded={sectionExpanded}
      onChange={onSectionChange}
      sx={{ '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<IconExpandMore style={{ fontSize: '1.05rem' }} />} sx={{ px: 2.5 }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <IconSettings style={{ fontSize: '1.3rem' }} />
          <Typography variant="subtitle1">Nastavení uživatele</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <SettingAccordion
          icon={IconLock}
          label="Změnit heslo"
          description="Pošle odkaz pro nastavení nového hesla na tvůj e-mail - heslo se nemění hned teď, jen se odešle odkaz."
          actionLabel="Odeslat e-mail"
          onAction={handleChangePassword}
          error={passwordError}
          success={passwordSent ? 'Odkaz odeslán na e-mail.' : ''}
          expanded={expandedSetting === 'password'}
          onChange={handleSettingAccordionChange('password')}
        />

        {canSeePin && (
          <SettingAccordion
            icon={IconPin}
            enabled={hasPin}
            label="Změnit PIN"
            description={
              hasPin === false
                ? 'Zatím nemáte nastavený PIN - bez něj nejde potvrdit ani jeden podpis. Pošle odkaz pro jeho nastavení na tvůj e-mail.'
                : 'Pošle odkaz pro nastavení nového podpisového PIN na tvůj e-mail - PIN se nemění hned teď, jen se odešle odkaz.'
            }
            actionLabel="Odeslat e-mail"
            onAction={handleChangePin}
            error={pinError}
            success={pinSent ? 'Odkaz odeslán na e-mail.' : ''}
            expanded={expandedSetting === 'pin'}
            onChange={handleSettingAccordionChange('pin')}
          />
        )}

        {canSeePush && (
          <SettingAccordion
            icon={IconNotifications}
            enabled={pushEnabled}
            label="Push notifikace"
            description="Upozornění přímo v prohlížeči/na zařízení pro tento účet - fungují jen tady, ne na jiném zařízení, kde ses nepřihlásil/a."
            actionLabel={pushEnabled ? 'Vypnout' : 'Zapnout'}
            onAction={handlePushClick}
            busy={pushBusy}
            disabled={!isPushSupported()}
            error={pushError}
            expanded={expandedSetting === 'push'}
            onChange={handleSettingAccordionChange('push')}
          />
        )}

        {canSeeGps && (
          <SettingAccordion
            icon={IconLocation}
            enabled={gpsGranted}
            label="GPS lokace"
            description="Pomáhá při píchnutí v Docházce ověřit polohu bez nutnosti udávat důvod - jen zrcadlí oprávnění polohy tohoto prohlížeče."
            actionLabel="Povolit"
            onAction={handleGpsClick}
            busy={gpsBusy}
            disabled={gpsGranted || !navigator.geolocation}
            error={gpsError}
            expanded={expandedSetting === 'gps'}
            onChange={handleSettingAccordionChange('gps')}
          />
        )}

        <SettingAccordion
          icon={IconMail}
          enabled={emailNotifyEnabled}
          label="E-mail o přihlášení"
          description="Pošle e-mail při každém úspěšném přihlášení do tohoto účtu (kromě prvního rozpoznání nového zařízení, to chodí vždy). Výchozí stav je vypnuto."
          actionLabel={emailNotifyEnabled ? 'Vypnout' : 'Zapnout'}
          onAction={handleEmailNotifyClick}
          busy={emailNotifyBusy}
          error={emailNotifyError}
          expanded={expandedSetting === 'email'}
          onChange={handleSettingAccordionChange('email')}
        />

        {canSeeGps && (
          <SettingAccordion
            icon={IconPunchClock}
            enabled={attendanceReminderEnabled}
            label="Alarm docházky"
            description="Zde si můžete aktivovat upozornění na vyplnění ranní docházky. Pokud v daný čas nebudete mít zaznamenaný příchod ani schválenou absenci (dovolenou, nemoc, OČR, propustku apod.), aplikace vám pošle připomínkovou push notifikaci."
            actionLabel={attendanceReminderEnabled ? 'Vypnout' : 'Zapnout'}
            onAction={handleAttendanceReminderClick}
            busy={attendanceReminderBusy}
            error={attendanceReminderError}
            expanded={expandedSetting === 'attendance_reminder'}
            onChange={handleSettingAccordionChange('attendance_reminder')}
          />
        )}

        <Accordion
          disableGutters
          elevation={0}
          square
          expanded={expandedSetting === 'private_email'}
          onChange={handleSettingAccordionChange('private_email')}
          sx={{ '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<IconExpandMore style={{ fontSize: '0.9rem' }} />} sx={{ px: 2.5, minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1.25 }}>
              <StatusIcon icon={IconPrivateEmail} enabled={privateEmailIsSet ? Boolean(privateEmailConfirmedAt) : undefined} />
              <Typography variant="body2">Soukromý e-mail</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pt: 1.25, pb: 1.5, borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack sx={{ gap: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                Všechny dokumenty týkající se vaší osoby (např. docházka, předávací protokoly apod.), které podepíšete v aplikaci, vám
                budou automaticky odeslány na váš soukromý e-mail.
              </Typography>
              <Typography variant="caption" sx={{ color: 'error.main', textAlign: 'center' }}>
                Jakmile vám dokument do e-mailu dorazí, klikněte prosím na odkaz v e-mailu, čímž potvrdíte jeho přijetí, jinak nelze brát
                dokument jako doručený a je neplatný!
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                {privateEmailIsSet && privateEmailConfirmedAt
                  ? 'Ověřeno. Z bezpečnostních důvodů appka adresu nikde nezobrazuje - chcete-li si ji ověřit, zadejte PIN.'
                  : privateEmailIsSet
                    ? 'Adresa čeká na potvrzení - zkontrolujte e-mail a klikněte na odkaz. Dokud se nepotvrdí, appka na ni nic nepošle.'
                    : 'Zatím nezadáno - zadejte adresu, appka pošle potvrzovací odkaz.'}
              </Typography>
              {privateEmailIsSet && (
                <Button
                  size="small"
                  variant="text"
                  onClick={handleOpenReveal}
                  sx={{ textTransform: 'none', alignSelf: 'flex-start', px: 0 }}
                >
                  Zobrazit adresu (vyžaduje PIN)
                </Button>
              )}
              <TextField
                type="email"
                size="small"
                fullWidth
                placeholder={privateEmailIsSet ? 'nová adresa (přepíše stávající)' : 'jmeno@example.com'}
                value={privateEmailInput}
                onChange={(e) => setPrivateEmailInput(e.target.value)}
              />
              <Button
                fullWidth
                size="small"
                variant="outlined"
                onClick={handleRequestPrivateEmail}
                disabled={privateEmailBusy || !privateEmailInput.trim()}
                sx={{ textTransform: 'none' }}
              >
                {privateEmailBusy ? <CircularProgress size={16} /> : 'Odeslat potvrzovací e-mail'}
              </Button>
              {privateEmailError && (
                <Typography variant="caption" sx={{ color: 'error.main' }}>
                  {privateEmailError}
                </Typography>
              )}
              {!privateEmailError && privateEmailSuccess && (
                <Typography variant="caption" sx={{ color: 'success.main' }}>
                  {privateEmailSuccess}
                </Typography>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Dialog open={revealDialogOpen} onClose={handleCloseReveal} fullWidth maxWidth="xs">
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Zobrazit soukromý e-mail
            <IconButton onClick={handleCloseReveal} size="small">
              <IconClose style={{ fontSize: '1.3rem' }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 3 }}>
            {revealedPrivateEmail ? (
              <Typography variant="body1" sx={{ textAlign: 'center', wordBreak: 'break-all' }}>
                {revealedPrivateEmail}
              </Typography>
            ) : (
              <>
                <Stack sx={{ gap: 1, alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Zadejte PIN pro zobrazení
                  </Typography>
                  <PinKeypad value={revealPin} onChange={setRevealPin} />
                </Stack>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={revealBusy || !PIN_PATTERN.test(revealPin)}
                  onClick={handleRevealPrivateEmail}
                  sx={{ textTransform: 'none' }}
                >
                  {revealBusy ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : 'Ověřit a zobrazit'}
                </Button>
              </>
            )}
            {revealError && <Alert severity="error">{revealError}</Alert>}
          </DialogContent>
        </Dialog>

        {canSeeSignatures && (
          <SettingAccordion
            icon={IconSignature}
            enabled={hasPin === undefined || testConfirmed === undefined ? undefined : hasPin && testConfirmed}
            label="Test podpisu"
            description={
              hasPin === false
                ? 'Nejdřív si nastavte PIN pomocí "Změnit PIN" výše - bez něj nejde podpis potvrdit.'
                : testConfirmed === false
                  ? 'Test zatím není dokončený - otevřete e-mail, který vám appka poslala, a klikněte na "Potvrdit doručení". Bez toho se test nepovažuje za úspěšný.'
                  : 'Nakreslete podpis, potvrďte ho svým PINem a appka vám ho pro kontrolu pošle e-mailem - ověříte si tak, že celý tok podpisu opravdu funguje.'
            }
            actionLabel="Otevřít test podpisu"
            onAction={handleOpenSignatureTest}
            disabled={hasPin === false}
            success={signatureTestSuccess}
            error={signatureTestError}
            expanded={expandedSetting === 'signature_test'}
            onChange={handleSettingAccordionChange('signature_test')}
          />
        )}
      </AccordionDetails>

      {canSeeSignatures && (
        <Dialog
          open={signatureTestOpen}
          onClose={() => setSignatureTestOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={signatureModalFullScreen}
          PaperProps={{ sx: { display: 'flex', flexDirection: 'column' } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            Test podpisu
            <IconButton size="small" onClick={() => setSignatureTestOpen(false)} aria-label="zavřít">
              <IconClose style={{ fontSize: '1.3rem' }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <SignatureCapture
              contextType="account_signature_test"
              onCaptured={async (id) => {
                await handleSignatureCaptured(id);
                setSignatureTestOpen(false);
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </Accordion>
  );
}
